import { CommonModule, NgOptimizedImage } from '@angular/common';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Directive,
  effect,
  EventEmitter,
  inject,
  input,
  Input,
  InputSignal,
  OnInit,
  Output,
  signal,
  WritableSignal
} from '@angular/core';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { DateAdapter, MAT_DATE_FORMATS, MatDateFormats, MatNativeDateModule, NativeDateAdapter } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { ProviderService, SchoolsService, AppInsightsService } from '@k12/app-services';
import {
  ComponentType,
  EmployeeSchoolOption,
  FormFieldImages,
  InputDataType,
  InputLabel,
  InputType,
  Placeholder,
  PromptComponent,
  PromptInput,
  PromptInputOption,
  SchoolOption,
  SharedImages,
  State,
  Validation
} from '@k12/models';
import { OrderByPipe } from '@k12/pipes';
import { NgxMaskDirective } from 'ngx-mask';
import { counties, gradeLevels, Lea, lea, states } from '../basic-info-const';
import { FormInlineErrorComponent } from '../form-inline-error/form-inline-error.component';
import { SsnFieldComponent } from '../ssn-field/ssn-field.component';

// The following date formats are used in the form field component to support the
// InputDataType.ShortDate and InputDataType.FullDate data types.
// The SHORT_DATE_FORMATS and FULL_DATE_FORMATS constants define
// the expected input and display formats for these date types.
// The AppShortDateFormatDirective and AppFullDateFormatDirective
// directives provide the necessary configuration for the Angular Material
// datepicker to use these formats.

const SHORT_DATE_FORMATS: MatDateFormats = {
  parse: {
    // Keep parsing flexible; display controls what user sees.
    dateInput: { year: 'numeric', month: '2-digit', day: '2-digit' }
  },
  display: {
    // Expected: 10/28
    dateInput: { month: '2-digit', day: '2-digit' },
    monthYearLabel: { year: 'numeric', month: 'short' },
    dateA11yLabel: { month: 'long', day: 'numeric' },
    monthYearA11yLabel: { year: 'numeric', month: 'long' }
  }
};

const FULL_DATE_FORMATS: MatDateFormats = {
  parse: {
    // Expected input/display style: 10/28/2026
    dateInput: { year: 'numeric', month: '2-digit', day: '2-digit' }
  },
  display: {
    // Expected: 10/28/2026
    dateInput: { year: 'numeric', month: '2-digit', day: '2-digit' },
    monthYearLabel: { year: 'numeric', month: 'short' },
    dateA11yLabel: { year: 'numeric', month: 'long', day: 'numeric' },
    monthYearA11yLabel: { year: 'numeric', month: 'long' }
  }
};

@Directive({
  selector: 'input[appShortDateFormat]',
  standalone: true,
  providers: [
    { provide: DateAdapter, useClass: NativeDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: SHORT_DATE_FORMATS }
  ]
})
export class AppShortDateFormatDirective { }

@Directive({
  selector: 'input[appFullDateFormat]',
  standalone: true,
  providers: [
    { provide: DateAdapter, useClass: NativeDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: FULL_DATE_FORMATS }
  ]
})
export class AppFullDateFormatDirective { }

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    MatAutocompleteModule,
    MatButtonModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatInputModule,
    MatNativeDateModule,
    MatSelectModule,
    NgxMaskDirective,
    NgOptimizedImage,
    OrderByPipe,
    ReactiveFormsModule,
    AppShortDateFormatDirective,
    AppFullDateFormatDirective,
    FormInlineErrorComponent,
    SsnFieldComponent
  ],
  selector: 'lib-pc-form-field',
  styleUrls: ['./pc-form-field.component.scss'],
  templateUrl: './pc-form-field.component.html'
})
export class PcFormFieldComponent implements OnInit {
  @Input() configJson: PromptComponent = {
    componentType: ComponentType.FormFields,
    header: '',
    inputType: 'form_fields',
    instructions: '',
    label: '',
    order: 0,
    inputs: [
      {
        inputType: 'text',
        instructions: '',
        order: 0,
        label: 'Label 1',
        dataType: 'address',
        isRequired: true,
        options: []
      }
    ]
  };

  editFormFieldInput: InputSignal<boolean | undefined> = input<boolean>();
  enableAddTextInputButton: InputSignal<boolean | undefined> = input<boolean>();
  enrollmentBuilderView: InputSignal<boolean | undefined> = input<boolean>();
  formGroup: InputSignal<FormGroup> = input(new FormGroup({ textField: new FormControl() }));

  @Output() addNewFormField = new EventEmitter();
  @Output() cbConfig = new EventEmitter();
  @Output() openDrawer = new EventEmitter();
  @Output() removeFormField = new EventEmitter();
  @Output() resetFormFieldValues = new EventEmitter();

  public adminFormGroup: FormGroup = new FormGroup({ textField: new FormControl() });
  public ComponentType: typeof ComponentType = ComponentType;
  public displayAdminFormField: WritableSignal<boolean> = signal(false);
  public einMask: string = '00-0000000';
  public incomeMask: string = 'separator.2';
  public InputDataType: typeof InputDataType = InputDataType;
  public InputType: typeof InputType = InputType;
  public isEditDrawerOpen: WritableSignal<boolean> = signal(false);
  public InputLabel: typeof InputLabel = InputLabel;
  public phoneMask: string = '(000) 000-0000';
  public Placeholder: typeof Placeholder = Placeholder;
  public SharedImages: typeof SharedImages = SharedImages;
  public showAddInputButton: WritableSignal<boolean> = signal(false);
  public showInputOptions: WritableSignal<boolean> = signal(false);
  public textFieldImages: string[] = [
    FormFieldImages.SingleFormField, FormFieldImages.SingleTextArea, FormFieldImages.SingleDropdown
  ];
  public Validation: typeof Validation = Validation;

  private localTrackIdSeq = 0;

  private readonly cdr = inject(ChangeDetectorRef);
  private readonly providerService = inject(ProviderService);
  private readonly schoolsService = inject(SchoolsService);
  private readonly appInsightsService = inject(AppInsightsService);

  // Maps school id -> "code - name" label for displaySchoolOption, since the FormControl itself
  // holds only the id.
  private readonly schoolOptionLabelsById = new Map<string, string>();

  constructor() {
    effect(() => {
      if (this.editFormFieldInput()) {
        if (this.configJson.inputs?.length === 0) {
          this.showAddInputButton.set(true);
          this.showInputOptions.set(false);
        }

        if (this.configJson.componentType !== ComponentType.SingleFormField) {
          this.showAddInputButton.set(true);
        }
      } else {
        this.isEditDrawerOpen.set(false);
        this.showInputOptions.set(false);

        if (this.configJson.inputs?.length === 0) {
          this.showAddInputButton.set(true);
          this.showInputOptions.set(false);
        }

        if (this.configJson.inputs?.length > 0) {
          this.showAddInputButton.set(false);
        }
      }
    });
  }

  public ngOnInit(): void {
    if (this.enrollmentBuilderView() && this.configJson.inputs?.length !== 0) {
      this.displayAdminFormField.set(true);
    }

    this.configJson.inputs?.forEach((item) => {
      this.assignLocalTrackId(item);
      this.setDropdownOptions(item);
    });

    this.showAddInputButton.set(this.configJson.inputs?.length === 0);
    this.cbConfig.emit(this.configJson);
  }

  public onAddComponent(inputType: string): void {
    let selectedInputType;
    switch (inputType) {
      case (FormFieldImages.SingleFormField):
        selectedInputType = InputType.Text;
        break;
      case (FormFieldImages.SingleTextArea):
        selectedInputType = InputType.TextArea;
        break;
      case (FormFieldImages.SingleDropdown):
        selectedInputType = InputType.SingleSelect;
        break;
    }

    this.displayAdminFormField.set(true);
    this.openDrawer.emit(false);
    if (this.configJson.componentType === ComponentType.SingleFormField) {
      this.showInputOptions.set(false);
      this.showAddInputButton.set(false);
    } else {
      this.showAddInputButton.set(true);
    }

    const orderInput = (this.configJson.inputs.length === 0) ? 0 : this.configJson.inputs.length;
    // New inputs carry no identity; the server assigns an id when the prompt is saved.
    const newInput = {
      dataType: InputType.Text,
      inputType: selectedInputType,
      order: orderInput,
      label: `Label ${orderInput + 1}`,
      instructions: '',
      isRequired: true,
      options: [],
      localTrackId: this.nextLocalTrackId()
    };
    this.addNewFormField.emit(newInput);
  }

  public addInputField(): void {
    this.showAddInputButton.set(false);
    this.showInputOptions.set(true);
    this.openDrawer.emit(false);
  }

  public openEditDrawer(index: number): void {
    this.isEditDrawerOpen.set(true);
    this.resetFormFieldValues.emit(false);
    this.openDrawer.emit({ openDrawer: true, editIndex: index });
  }

  public removeField(index: number): void {
    this.removeFormField.emit(index);
    if (this.configJson.componentType === ComponentType.SingleFormField) {
      this.showAddInputButton.set(false);
      this.showInputOptions.set(true);
      this.isEditDrawerOpen.set(false);
      this.resetFormFieldValues.emit(true);
    } else if (this.configJson.inputs.length === 0) {
      this.showAddInputButton.set(false);
      this.showInputOptions.set(true);
    }
    this.openDrawer.emit(false);
  }

  public onDateSelected(value: Date | null, controlName: string): void {
    const control = this.formGroup()?.get(controlName);
    if (!control) {
      return;
    }

    control.setValue(value ?? null, { emitEvent: true });
    control.markAsDirty();
    control.markAsTouched();
    control.updateValueAndValidity({ emitEvent: true });
  }

  public getInputMaxLength(input: InputDataType): number | null {
    if (input === InputDataType.ZipCode) {
      return 5;
    } else if (input === InputDataType.HHSize) {
      return 2;
    }
    return null;
  }

  public isMaskedDataType(dataType: string): boolean {
    return dataType === InputDataType.Phone || dataType === InputDataType.EIN || dataType === InputDataType.HHIncome || dataType === InputDataType.SSN9;
  }

  public getInputMask(input: string): string | undefined {
    if (input === InputDataType.Phone) {
      return this.phoneMask;
    } else if (input === InputDataType.EIN) {
      return this.einMask;
    } else if (input === InputDataType.HHIncome) {
      return this.incomeMask;
    }
    return undefined;
  }

  public getDecimalMarker(dataType: string): '.' | ',' | ['.', ','] {
    // Keep a valid non-null value for ngx-mask
    return dataType === InputDataType.HHIncome ? '.' : ['.', ','];
  }

  public getThousandSeparator(dataType: string): string {
    return dataType === InputDataType.HHIncome ? ',' : '';
  }

  public getPrefix(dataType: string): string {
    return dataType === InputDataType.HHIncome ? '$' : '';
  }

  public getTextareaRows(charLength: number): number {
    if (charLength <= 100) return 2;
    if (charLength <= 200) return 3;
    if (charLength <= 300) return 4;
    if (charLength <= 400) return 5;
    return 7; // For charLength > 400
  }

  /** Shows "code - name" once a school is selected, even though the FormControl holds only the id. */
  public displaySchoolOption = (value: string | null | undefined): string => {
    if (value === null || value === undefined || value === '') {
      return '';
    }
    return this.schoolOptionLabelsById.get(String(value)) ?? '';
  };

  /** Client-side filter over the pre-fetched school catalog (~6,000 rows, too large for mat-select). */
  public getFilteredSchoolOptions(input: PromptInput): PromptInputOption[] {
    const options = input.options ?? [];
    const rawValue = this.formGroup()?.get(input.id ?? '')?.value;
    const searchTerm = typeof rawValue === 'string' ? rawValue.trim().toLowerCase() : '';

    if (!searchTerm) {
      return options;
    }

    return options.filter((option) => option.label.toLowerCase().includes(searchTerm));
  }

  // Gives unsaved inputs (no server-assigned id) a stable identity, set once at creation, so
  // the template can track them by plain property access instead of by list position -- see
  // PromptInput.localTrackId and the identical pattern in PcCheckboxComponent.
  private assignLocalTrackId(input: PromptInput): void {
    if (!input.id && !input.localTrackId) {
      input.localTrackId = this.nextLocalTrackId();
    }
  }

  private nextLocalTrackId(): string {
    return `local_${this.localTrackIdSeq++}`;
  }

  private setDropdownOptions(input: PromptInput) {
    switch (input.placeholder) {
      case Placeholder.County:
        input.options = counties.map((county: string, index: number): PromptInputOption => {
          return { dataType: InputType.Text, label: county, order: index }
        });
        input.options.sort((a, b) => a.label.localeCompare(b.label));
        input.options.unshift({ dataType: InputType.Text, label: 'Out of State', order: -1 });
        this.refreshDropdownControlValue(input);
        break;
      case Placeholder.GradeLevel:
        input.options = gradeLevels.map((gradeLevel: string, index: number): PromptInputOption => {
          return { dataType: InputType.Text, label: gradeLevel, order: index }
        });
        this.refreshDropdownControlValue(input);
        break;
      case Placeholder.School:
        this.schoolsService.getSchoolsForDropdown('', true).subscribe({
          next: (res) => {
            input.options = res.entity.map((entity: SchoolOption, index: number) => {
              return { dataType: InputType.Text, label: entity.name, value: entity.id, order: index }
            })
            input.options.push({ dataType: InputType.Text, label: 'Unknown', value: 'unknown', order: input.options.length + 1 });
            this.refreshDropdownControlValue(input);
          },
          error: (error: unknown) => this.appInsightsService.log(error instanceof Error ? error : new Error(String(error)))
        });
        break;
      case Placeholder.SchoolDistrict:
        input.options = lea.map((lea: Lea, index: number): PromptInputOption => {
          return { dataType: InputType.Text, label: lea.leaName, order: index, value: lea.leaId };
        });
        input.options.sort((a, b) => a.label.localeCompare(b.label));
        this.refreshDropdownControlValue(input);
        break;
      case Placeholder.ProviderDegreeSchool:
        this.providerService.getSchools().subscribe({
          next: (schools: EmployeeSchoolOption[]) => {
            input.options = schools.map((school: EmployeeSchoolOption, index: number): PromptInputOption => {
              const label = `${school.schoolCode} - ${school.schoolName}`;
              this.schoolOptionLabelsById.set(String(school.id), label);
              return { dataType: InputType.Text, label, order: index, value: school.id };
            });
            this.refreshDropdownControlValue(input);
          },
          error: (error: unknown) => this.appInsightsService.log(error instanceof Error ? error : new Error(String(error)))
        });
        break;
      case Placeholder.State:
        input.options = states.map((state: State, index: number) => {
          return { dataType: InputType.Text, label: state.name, order: index, value: state.code };
        });
        this.refreshDropdownControlValue(input);
        break;
      default:
        return;
    }
  }

  /** Re-evaluates the FormControl's value for a dropdown input, in case the options have
   * changed (e.g. after an async fetch) and the current value is no longer valid.
   * */
  private refreshDropdownControlValue(input: PromptInput): void {
    const controlName = input.id ?? '';
    const control = this.formGroup()?.get(controlName);
    const currentValue = control?.value;
    if (control && currentValue !== null && currentValue !== undefined && currentValue !== '') {
      control.setValue(currentValue, { emitEvent: false });
    }
    this.cdr.markForCheck();
  }
}
